import React, { useState } from 'react';
import { COLORS, BORDER_RADIUS, SHADOWS } from '../../../styles/UISystem';
import { CaretLeft } from '@phosphor-icons/react/dist/ssr/CaretLeft';
import { Phone } from '@phosphor-icons/react/dist/ssr/Phone';
import StatusSubtitle from '../StatusSubtitle';
import { ChatCircle } from '@phosphor-icons/react/dist/ssr/ChatCircle';
import { Envelope } from '@phosphor-icons/react/dist/ssr/Envelope';
import { InstagramLogo } from '@phosphor-icons/react/dist/ssr/InstagramLogo';
import { LinkedinLogo } from '@phosphor-icons/react/dist/ssr/LinkedinLogo';
import { WhatsappLogo } from '@phosphor-icons/react/dist/ssr/WhatsappLogo';
import { CopySimple } from '@phosphor-icons/react/dist/ssr/CopySimple';
import { IconCircleButton } from '../Icons/IconCircleButton';
import { formatPhoneForWhatsApp } from '../../../utils/formatters';

/**
 * HeaderConnectionDetail - Frosted glass header for connection detail pages
 *
 * Shows profile image with name pill below, contact icons, and back button.
 * Creates a "business card" effect with identity + contact methods together.
 *
 * @param {Object} props - Component props
 * @param {string} props.name - Full name to display in pill
 * @param {string} props.imageUrl - URL of the profile image
 * @param {string} props.initialLetter - Initial letter to display when no image
 * @param {function} props.onBackClick - Function called when back button is clicked
 * @param {function} props.onImageClick - Function called when profile image is clicked (optional)
 * @param {boolean} props.showPlaceholder - Whether to show placeholder when no data
 * @param {Object} props.userData - User data containing contact info
 * @param {Array} props.sharedFields - Array of field names that have been shared
 * @returns {JSX.Element}
 */
const HeaderConnectionDetail = ({
  name = '',
  imageUrl = '',
  initialLetter = '',
  onBackClick,
  onImageClick,
  showPlaceholder = true,
  userData = null,
  sharedFields = [],
  activityStatus = null,
  locationDisplay = null,
  hometown = null,
}) => {
  const [showCopied, setShowCopied] = useState(false);

  // Helper to check if a contact field should be shown
  const shouldShowContact = (fieldName, fieldValue) => {
    return fieldValue && sharedFields?.includes(fieldName);
  };

  // Copy "Other" contact value to clipboard
  const handleCopyOther = async () => {
    if (userData?.other) {
      try {
        await navigator.clipboard.writeText(userData.other);
        setShowCopied(true);
        setTimeout(() => setShowCopied(false), 1500);
      } catch (err) {
        console.error('Failed to copy:', err);
      }
    }
  };

  // Check if any contacts are available to show
  const hasAnyContacts =
    shouldShowContact('phone', userData?.phone) ||
    shouldShowContact('email', userData?.email) ||
    shouldShowContact('instagram', userData?.instagram) ||
    shouldShowContact('linkedin', userData?.linkedin) ||
    shouldShowContact('whatsapp', userData?.whatsapp) ||
    shouldShowContact('other', userData?.other);
  return (
    <div
      style={{
        position: 'relative',
        width: '100%',
        backgroundColor: 'rgba(255, 255, 255, 0.85)', // Semi-transparent white for frosted glass effect
        backdropFilter: 'blur(20px)', // iOS-style blur
        WebkitBackdropFilter: 'blur(20px)', // Safari support
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        paddingTop: '4px',
        paddingBottom: '6px',
      }}
    >
      {/* Top row: Back button and Profile image */}
      <div
        style={{
          width: '100%',
          height: '44px', // Fixed height matching ConnectionImage
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          paddingLeft: '16px',
          paddingRight: '16px',
          marginBottom: '2px',
        }}
      >
        {/* Back button - left side (white pill with caret only, matches Connection Page style) */}
        <div
          onClick={onBackClick}
          style={{
            position: 'absolute',
            left: '16px',
            display: 'flex',
            alignItems: 'center',
            padding: '8px 10px',
            borderRadius: BORDER_RADIUS.PILL,
            backgroundColor: '#FFFFFF',
            boxShadow: SHADOWS.TIER1,
            cursor: 'pointer',
          }}
        >
          <CaretLeft size={26} color="#555555" weight="bold" />
        </div>

        {/* Profile image - center (absolutely positioned) */}
        <div
          onClick={onImageClick}
          style={{
            cursor: onImageClick ? 'pointer' : 'default',
            position: 'absolute',
            left: '50%',
            transform: 'translateX(-50%)',
            top: 0,
          }}
        >
          <div
            style={{
              width: '80px', // Larger than ConnectionImage (60px)
              height: '80px',
              borderRadius: '50%',
              backgroundColor: COLORS.MEDIUM_GRAY,
              overflow: 'hidden',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            {imageUrl ? (
              <img
                src={imageUrl}
                alt="Profile"
                onError={(e) => {
                  console.log('[HEADER_DETAIL_DEBUG] Image failed to load:', imageUrl);
                  e.target.style.display = 'none';
                }}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                }}
              />
            ) : initialLetter ? (
              <div
                style={{
                  fontSize: '36px', // Larger font for bigger circle
                  fontWeight: 'bold',
                  color: COLORS.WHITE,
                }}
              >
                {initialLetter}
              </div>
            ) : showPlaceholder ? (
              <div
                style={{
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  backgroundColor: COLORS.MEDIUM_GRAY,
                }}
              />
            ) : null}
          </div>
        </div>
      </div>

      {/* Name - centered below profile image (no pill background) */}
      <div
        style={{
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
          marginTop: '40px', // 80px image - 44px row + 4px padding
        }}
      >
        {name && name.trim() !== '' ? (
          <span
            style={{
              fontSize: '24px',
              fontWeight: '700',
              color: '#000000',
              letterSpacing: '-0.3px',
              cursor: onImageClick ? 'pointer' : 'default',
            }}
            onClick={onImageClick}
          >
            {name}
          </span>
        ) : showPlaceholder ? (
          <div
            style={{
              height: '28px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          />
        ) : null}
      </div>

      {/* Status subtitle — location, hometown, or activity */}
      <div style={{ display: 'flex', justifyContent: 'center', marginTop: '2px' }}>
        <StatusSubtitle
          locationDisplay={locationDisplay}
          hometown={hometown}
          activityStatus={activityStatus}
          size={14}
        />
      </div>

      {/* Contact icons row - centered below name */}
      {hasAnyContacts && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            gap: '12px',
            marginTop: '8px',
          }}
        >
          {/* Phone icon */}
          {shouldShowContact('phone', userData?.phone) && (
            <IconCircleButton
              icon={Phone}
              size={24}
              buttonSize={40}
              href={`tel:${userData.phone.replace(/[^0-9+]/g, '')}`}
            />
          )}

          {/* SMS icon */}
          {shouldShowContact('phone', userData?.phone) && (
            <IconCircleButton
              icon={ChatCircle}
              size={24}
              buttonSize={40}
              href={`sms:${userData.phone.replace(/[^0-9+]/g, '')}`}
            />
          )}

          {/* Email icon */}
          {shouldShowContact('email', userData?.email) && (
            <IconCircleButton
              icon={Envelope}
              size={24}
              buttonSize={40}
              href={`mailto:${userData.email}`}
            />
          )}

          {/* Instagram icon */}
          {shouldShowContact('instagram', userData?.instagram) && (
            <IconCircleButton
              icon={InstagramLogo}
              size={24}
              buttonSize={40}
              href={`https://instagram.com/${userData.instagram.startsWith('@') ? userData.instagram.substring(1) : userData.instagram}`}
              target="_blank"
              rel="noopener noreferrer"
            />
          )}

          {/* LinkedIn icon */}
          {shouldShowContact('linkedin', userData?.linkedin) && (
            <IconCircleButton
              icon={LinkedinLogo}
              size={24}
              buttonSize={40}
              href={`https://linkedin.com/in/${userData.linkedin}`}
              target="_blank"
              rel="noopener noreferrer"
            />
          )}

          {/* WhatsApp icon */}
          {shouldShowContact('whatsapp', userData?.whatsapp) && (
            <IconCircleButton
              icon={WhatsappLogo}
              size={24}
              buttonSize={40}
              href={`https://wa.me/${formatPhoneForWhatsApp(userData.whatsapp)}`}
              target="_blank"
              rel="noopener noreferrer"
            />
          )}

          {/* "Other" contact - copyable value with label */}
          {shouldShowContact('other', userData?.other) && (
            <div
              style={{
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              {/* Copyable value field */}
              <div
                onClick={handleCopyOther}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  padding: '8px 12px',
                  backgroundColor: '#F0F0F2',
                  borderRadius: '16px',
                  cursor: 'pointer',
                }}
              >
                <span
                  style={{
                    fontSize: '14px',
                    color: '#6D7074',
                    maxWidth: '150px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {userData.other}
                </span>
                <CopySimple size={14} color="#8E8E93" weight="regular" />
              </div>

              {/* Label to the right */}
              <span
                style={{
                  fontSize: '14px',
                  color: '#8E8E93',
                }}
              >
                {userData.otherLabel || 'Other'}
              </span>

              {/* "Copied!" tooltip */}
              {showCopied && (
                <div
                  style={{
                    position: 'absolute',
                    top: '-28px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    backgroundColor: '#333',
                    color: '#fff',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    fontSize: '12px',
                    whiteSpace: 'nowrap',
                  }}
                >
                  Copied!
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default HeaderConnectionDetail;
